// @flow

import React, {useRef, useEffect, memo, useContext, forwardRef} from 'react';
import {FloatingPortal, useFloating, autoUpdate, offset} from '@floating-ui/react';
import {useCallbackWithRef} from '@pb/kit-util-use-callback-with-ref';
import {SORTABLE_POSITION} from '@pb/kit-component-native-sortable';
import {colors} from '@pb/kit-stylus/config';
import {useShown} from '@pb/kit-hooks-use-shown';
import {withCssModule, hashedClasses} from '@pb/kit-util-with-css-module';

import {createStyleVerticalLine} from '../utils';
import {MouseObserverContext} from './contexts';
import {DataTableContext} from '../contexts/data-table-context';
import {DataTableHeaderContext} from '../contexts/data-table-header-context';
import styles from './insert-column-button.module.styl';

import type {ComponentType, ElementConfig} from 'react';

import type {MouseObserverContextType} from './contexts';
import type {
    OnInsertCustomColumn,
    OnMouseMove
} from './typedef';
import type {DataTableContextType} from '../contexts/data-table-context';
import type {DataTableHeaderContextType} from '../contexts/data-table-header-context';

const classNames = withCssModule(styles);
const classes = hashedClasses(styles);

const THRESHOLD = 10;

type NativeButtonProps = ElementConfig<'div'>;

type InsertButtonProps = NativeButtonProps & {
    index: number,
    onClose: () => void,
    onInsertColumn: OnInsertCustomColumn,
    isSticky: boolean,
};

const InsertButton = forwardRef(({index, onClose, onInsertColumn, isSticky, ...props}: InsertButtonProps, ref) => {
    const styleRef = useRef<HTMLStyleElement | null>(null);

    const removeStyle = () => {
        if (styleRef.current) {
            (document.head || {}).removeChild(styleRef.current);

            styleRef.current = null;
        }
    };

    const addStyle = () => {
        const style = createStyleVerticalLine({
            position: SORTABLE_POSITION.BEFORE,
            columnIndex: index,
            colorLine: colors.primaryColors.skyBlue
        });

        (document.head || {}).appendChild(style);

        styleRef.current = style;
    };

    const onClick = () => {
        removeStyle();
        onInsertColumn();
        onClose();
    };

    useEffect(() => removeStyle, []);

    const className = classNames('insert_column_button', {
        'is-sticky': isSticky,
    });

    return (
        <div {...props} {...{className}} ref={ref}>
            <button {...{
                onMouseLeave: removeStyle,
                onMouseEnter: addStyle,
                onClick,
                type: 'button',
                className: classes.insert_column_button__button
            }} >
                <div {...{className: classes.insert_column_button__icon}}>
                    +
                </div>
            </button>
        </div>
    );
});

InsertButton.displayName = 'InsertColumnButton';

type Props = {|
    onInsertColumn: OnInsertCustomColumn,
    element: HTMLElement,
    index: number,
|};

export const InsertColumnButton: ComponentType<Props> = memo<Props>(function InsertColumnButton(props: Props) {
    const {
        onInsertColumn,
        element,
        index
    } = props;

    const {shown, onClick: onOpen, onClose} = useShown();

    const {subscribe} = useContext<MouseObserverContextType>(MouseObserverContext);

    const onMouseMove = useCallbackWithRef<OnMouseMove>(position => {
        const {pageX} = position;

        if (!pageX) {
            onClose();

            return;
        }

        const {left, right} = element.getBoundingClientRect();

        const doc = document.documentElement || {};

        const width = doc.offsetWidth;

        const offsetAreaX = right < width && pageX > left + THRESHOLD && pageX < right + THRESHOLD;

        if (offsetAreaX) {
            onOpen();
        } else {
            onClose();
        }
    });

    useEffect(() => subscribe(onMouseMove), [onMouseMove, subscribe]);

    const {dataTableRef} = useContext<DataTableContextType>(DataTableContext);
    const {isSticky} = useContext<DataTableHeaderContextType>(DataTableHeaderContext);

    const {refs, floatingStyles} = useFloating({
        open: shown,
        placement: 'top-end',
        strategy: 'fixed',
        whileElementsMounted: autoUpdate,
        elements: {
            reference: element
        },
        middleware: [
            offset({
                crossAxis: 15,
                mainAxis: -5
            })
        ],
    });

    if (!shown) {
        return null;
    }
    return (
        <FloatingPortal root={isSticky ? undefined : dataTableRef.current}>
            <InsertButton
                {...{
                    onInsertColumn,
                    index,
                    onClose,
                    isSticky
                }}
                ref={refs.setFloating}
                style={floatingStyles}
            />
        </FloatingPortal>
    );
});

InsertColumnButton.displayName = 'InsertColumnButton';

            // @pb/generate-flow-typed Webpack Loader:
            // Duplicates all internal dependencies imports
            // to be sure all deps are parsed by this loader
            // cause Babel loader removes `import type ...`
            // and e.g. 'typedef.js' files are not included in dist
            
require("./contexts");

require("./typedef");

require("../contexts/data-table-context");

require("../contexts/data-table-header-context");
